'use client';

import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { X, Check, User, Clock, Star, Zap, Loader2 } from 'lucide-react';
import ReactSlider from 'react-slider';
import { Amenity, ListingType } from 'hotdesk_typescript';
import { useAppDispatch, useAppSelector } from '@/hotdesk-ui/hooks/hook';
import { RootState } from '@/hotdesk-ui/store/store';
import { FilterValues, isQuickBookingActive, useFilterContext } from '../filters/FiltersContext';
import { getListingData, getWorkspaceData, setActiveButton, setFilters, setListingPage, setWorkspacePage } from '@/hotdesk-ui/store/slices/listing.slice';
import { ApiConfigration } from '@/hotdesk-ui/common-classes/api-configuration';
import { ListingService } from '@/hotdesk-ui/services/listing.service';
import { firebaseAnalytics } from '@/hotdesk-ui/common-classes/firebase.service';
import { Carousel } from '@/hotdesk-ui/enum/listing-workspace';
import { applyGeoToSearchParams, filterIwgQuoteOnlyListings, geoAreaScope, isLongTermFrequency, LONG_TERM_IWG_QUOTE_FREQUENCY } from '@/hotdesk-ui/helpers/explore-location-params';
import { getListingProviderCode } from '@/hotdesk-ui/utils/provider';
import { getSubscriptionName, getActiveUserSubscriptions, groupUserSubscriptionsByPackageName, EXPLORE_SUBSCRIPTION_OPT_OUT, buildExploreFetchParams } from '@/hotdesk-ui/utils/subscriptionUtils';
import { convertBookingPriceFromUsd as convertPriceFromUsd, convertBookingPriceToUsd as convertPriceToUsd } from '@/hotdesk-ui/helpers/booking-filter-price';
import { BOOKING_PLAN_PRICE_USD_FALLBACKS as PLAN_PRICE_USD_FALLBACKS, getDisplayPriceBoundsForPlan, loadAllBookingPlanPriceBoundsFromRemoteConfig as loadPlanPriceBoundsUsd, type BookingFilterPlan as FilterPlan, type BookingPlanUsdBounds as PlanUsdBounds } from '@/hotdesk-ui/helpers/booking-filter-price-bounds';
import { useSelector } from 'react-redux';
import styles from './ExploreFiltersModal.module.scss';
const Slider = ReactSlider as unknown as React.FC<any>;
const PLAN_OPTIONS = [{
  labelKey: 'filters.time.hour',
  value: 'hour'
}, {
  labelKey: 'filters.time.daily',
  value: 'day',
  iconSrc: '/assets/icons/daily.svg'
}, {
  labelKey: 'filters.time.montly',
  value: 'month',
  iconSrc: '/assets/icons/monthly.svg'
}, {
  labelKey: 'filters.time.annual',
  value: 'annual',
  iconSrc: '/assets/icons/Newannualy.svg'
}];
const PAYMENT_OPTIONS = [{
  labelKey: 'filters.payment-method.online',
  value: 'credit card'
}, {
  labelKey: 'filters.payment-method.host',
  value: 'cash'
}];

/** Slider range before a plan is picked (the slider is disabled until then). */
const DEFAULT_PRICE_CEIL = 5000;
/**
 * Filter keys this modal owns in the URL — cleared before re-writing.
 * `currency` is never written any more (the API's `currency` param 502s), but
 * stays here so it gets scrubbed from links produced by earlier builds.
 */
const URL_FILTER_KEYS = ['typeIn', 'frequency', 'priceGte', 'priceLte', 'currency', 'amenitiesIdIn', 'paymentTypeIn', 'filterCapOrQtyGte', 'quickBooking', 'ratingGte', 'userSubscriptionIdIn'];
function buildCleanedFilters(filterValues: FilterValues): Record<string, string> {
  const cleaned: Record<string, string> = {};
  Object.entries(filterValues).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    if (k === 'quickBooking') {
      if (isQuickBookingActive(v)) cleaned[k] = 'true';
      return;
    }
    if (Array.isArray(v)) {
      if (v.length) cleaned[k] = v.join(',');
    } else {
      cleaned[k] = String(v);
    }
  });
  return cleaned;
}
function buildAppliedFilters(filterValues: FilterValues, draftSubscriptions: string[]): Record<string, string> {
  const cleaned = buildCleanedFilters(filterValues);
  delete cleaned.userSubscriptionIdIn;
  // Price is held in modal-local state and re-attached by `attachPrice`.
  // Writing it to FilterContext on every drag step re-rendered the whole
  // explore tree (grid + map) hundreds of times per drag.
  delete cleaned.priceGte;
  delete cleaned.priceLte;
  delete cleaned.currency;
  if (draftSubscriptions.length) {
    cleaned.userSubscriptionIdIn = draftSubscriptions.join(',');
  }
  return cleaned;
}

/**
 * Slider granularity for the plan's range: ~200 stops, snapped to a round
 * number. A fixed step can't work here — the range spans 2k (hourly, USD) to
 * 200k+ (annual, or any weak currency).
 */
function priceStepFor(min: number, max: number): number {
  const span = max - min;
  if (!Number.isFinite(span) || span <= 0) return 1;
  const raw = span / 200;
  const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
  return Math.max(1, Math.round(raw / magnitude) * magnitude);
}
function resolveActiveCarousel(cleaned: Record<string, string>): Carousel {
  return cleaned.typeIn || cleaned.frequency || cleaned.quickBooking ? Carousel.listing : Carousel.workspace;
}

/**
 * Workspace preview needs enough listing rows to unique-count buildings
 * (`distinct=workspace` reports listing `count`). Listing preview only
 * needs `count`, so a 1-row page is enough.
 */
const PREVIEW_WORKSPACE_LIMIT = 100;

/**
 * Long-term (month/annual) explore appends IWG/Regus Request Quote cards from
 * a secondary day/hour fetch (see ExploreClient). The preview mirrors that so
 * "Show Results" counts those cards too, and needs listing rows to dedupe.
 */
const PREVIEW_IWG_QUOTE_LIMIT = 100;
function buildPreviewRequestParams(service: ListingService, mode: Carousel, cleaned: Record<string, string>, options: {
  country?: string;
  city?: string;
  geoLat?: number | null;
  geoLog?: number | null;
  /** ISO-2 code when the searched place is a whole country or state. */
  geoCountryCode?: string | null;
  /** State name when the searched place is a whole state. */
  geoRegion?: string | null;
  radius: number;
  limit?: number;
}) {
  const {
    country,
    city,
    geoLat,
    geoLog,
    geoCountryCode,
    geoRegion,
    radius
  } = options;
  const previewLimit = options.limit ?? (mode === Carousel.workspace ? PREVIEW_WORKSPACE_LIMIT : 1);
  if (country && city) {
    return service.getListingsDataParams(mode, previewLimit, 0, null, cleaned, country, city);
  }
  if (country) {
    return service.getListingsDataParams(mode, previewLimit, 0, null, cleaned, country);
  }
  // Whole-country search (e.g. "Japan"), as getListingData does: a radius
  // around the country's centroid finds nothing, so count by country code
  // (and by state name within it for a whole state).
  if (geoCountryCode) {
    return service.getListingsDataParams(mode, previewLimit, 0, null, cleaned, undefined, undefined, undefined, geoCountryCode, geoRegion ?? undefined);
  }
  if (geoLat != null && geoLog != null) {
    return service.getListingsDataParams(mode, previewLimit, 0, {
      lat: geoLat,
      lng: geoLog,
      radius
    }, cleaned);
  }
  return undefined;
}
export interface ExploreFiltersModalProps {
  open: boolean;
  onClose: () => void;
  allAminities: Amenity[];
  listingRes: ListingType[];
  type: 'country' | 'city' | 'workspace';
  country?: string;
  city?: string;
}
const Checkbox: FC<{
  checked: boolean;
  onChange: () => void;
  children: React.ReactNode;
}> = ({
  checked,
  onChange,
  children
}) => <label className={styles.optionRow} data-sentry-component="Checkbox" data-sentry-source-file="ExploreFiltersModal.tsx">
        <input type="checkbox" checked={checked} onChange={onChange} style={{
    position: 'absolute',
    opacity: 0,
    pointerEvents: 'none'
  }} aria-hidden="true" />
        <span className={`${styles.check} ${checked ? styles.checked : ''}`}>
            {checked && <Check size={14} />}
        </span>
        {children}
    </label>;
const Radio: FC<{
  checked: boolean;
  onChange: () => void;
  children: React.ReactNode;
}> = ({
  checked,
  onChange,
  children
}) => <label className={styles.optionRow} data-sentry-component="Radio" data-sentry-source-file="ExploreFiltersModal.tsx">
        <input type="radio" checked={checked} onChange={onChange} style={{
    position: 'absolute',
    opacity: 0,
    pointerEvents: 'none'
  }} aria-hidden="true" />
        <span className={`${styles.radio} ${checked ? styles.radioChecked : ''}`} />
        {children}
    </label>;
const ExploreFiltersModal: FC<ExploreFiltersModalProps> = ({
  open,
  onClose,
  allAminities,
  listingRes,
  type,
  country,
  city
}) => {
  const t = useTranslations();
  const dispatch = useAppDispatch();
  const {
    filterValues,
    setFilterValues,
    processFilters
  } = useFilterContext();
  const geoLocation = useAppSelector((s: RootState) => s.geoLocation.geoLocationZipData);
  const activeButton = useAppSelector((s: RootState) => s.listing.activeButton);
  const reduxFilters = useAppSelector((s: RootState) => s.listing.filters);
  const defaultCurrency = useAppSelector((s: RootState) => s.currency.defaultCurrency);
  const currencyList = useAppSelector((s: RootState) => s.currency.currency);
  const defaultLanguage = useAppSelector((s: RootState) => s.listing.defaultLanguage);
  const radius = useAppSelector((s: RootState) => s.listing.radius);
  const geoLat = geoLocation?.lat ?? null;
  const geoLog = geoLocation?.log ?? null;
  const geoScope = geoAreaScope(geoLocation);
  const geoCountryCode = geoScope?.countryCode ?? null;
  const geoRegion = geoScope?.region ?? null;
  const previewRequestRef = useRef(0);
  const openRef = useRef(open);
  openRef.current = open;
  const user = useSelector((state: RootState) => state.auth.user);
  const subscriptionName = useMemo(() => getSubscriptionName(user), [user]);
  const subscriptionOptions = useMemo(() => groupUserSubscriptionsByPackageName(getActiveUserSubscriptions(user)), [user]);

  // Keep FilterContext in sync with Redux (e.g. when pills change typeIn)
  useEffect(() => {
    const reduxTypeIn = reduxFilters.typeIn || '';
    const ctxTypeIn = Array.isArray(filterValues.typeIn) ? filterValues.typeIn.join(',') : filterValues.typeIn || '';
    if (reduxTypeIn !== ctxTypeIn) {
      setFilterValues((prev: FilterValues) => ({
        ...prev,
        typeIn: reduxTypeIn ? reduxTypeIn.split(',') : []
      }));
    }
  }, [reduxFilters.typeIn]);

  // Keep the modal draft in sync with the explore chip (same `quickBooking` filter).
  useEffect(() => {
    const reduxQuick = reduxFilters.quickBooking === 'true';
    if (reduxQuick !== isQuickBookingActive(filterValues.quickBooking)) {
      setFilterValues((prev: FilterValues) => ({
        ...prev,
        quickBooking: reduxQuick
      }));
    }
  }, [reduxFilters.quickBooking]);
  const [draftSubscriptions, setDraftSubscriptions] = useState<string[]>([]);
  useEffect(() => {
    if (!open) return;
    const raw = reduxFilters.userSubscriptionIdIn;
    if (raw === EXPLORE_SUBSCRIPTION_OPT_OUT) {
      setDraftSubscriptions([]);
    } else if (raw) {
      setDraftSubscriptions(raw.split(',').filter(Boolean));
    } else {
      // Opt-in, not pre-selected: subscription filter is off by default.
      setDraftSubscriptions([]);
    }
  }, [open]);
  const [draftGuests, setDraftGuests] = useState<number>(Number(filterValues.filterCapOrQtyGte) || 1);
  const [priceRange, setPriceRange] = useState<[number, number]>([Number(filterValues.priceGte ?? 0), Number(filterValues.priceLte ?? DEFAULT_PRICE_CEIL)]);
  /** False until the user actually narrows the range — see `attachPrice`. */
  const [priceTouched, setPriceTouched] = useState(false);
  const [priceError, setPriceError] = useState<string | null>(null);
  /**
   * Raw text while a price input is being edited. `null` means "show the
   * number"; a string lets the field go empty so the user can erase the
   * current value instead of fighting a re-rendered `0`.
   */
  const [minInputText, setMinInputText] = useState<string | null>(null);
  const [maxInputText, setMaxInputText] = useState<string | null>(null);
  /** Set when the user pokes the price controls before picking a plan. */
  const [planRequiredWarning, setPlanRequiredWarning] = useState(false);
  const planSectionRef = useRef<HTMLElement | null>(null);
  const [planPriceBoundsUsd, setPlanPriceBoundsUsd] = useState<PlanUsdBounds>(PLAN_PRICE_USD_FALLBACKS);
  const boundsRequestedRef = useRef(false);
  const [previewResultCount, setPreviewResultCount] = useState<number | null>(null);
  const [previewCountLoading, setPreviewCountLoading] = useState(false);
  const selectedPlans = (filterValues.frequency || '').split(',').map(v => v.trim()).filter(Boolean);
  const planSelected = selectedPlans.length > 0;
  // Price bounds are per-plan; with multiple plans (e.g. month+annual from
  // Long-term offices) fall back to the default slider range.
  const selectedPlan = (selectedPlans.length === 1 ? selectedPlans[0] : '') as FilterPlan;

  // Per-plan price bounds (Remote Config, in USD). Without these the slider
  // spanned a flat 0–100000 for every plan, so nudging the min handle on an
  // hourly search jumped straight past every real price and returned nothing.
  useEffect(() => {
    if (!open || boundsRequestedRef.current) return;
    boundsRequestedRef.current = true;
    let cancelled = false;
    loadPlanPriceBoundsUsd().then(bounds => {
      if (!cancelled) setPlanPriceBoundsUsd(bounds);
    });
    return () => {
      cancelled = true;
    };
  }, [open]);
  const priceBounds = useMemo(() => {
    if (!planSelected || !planPriceBoundsUsd[selectedPlan]) {
      return {
        min: 0,
        max: DEFAULT_PRICE_CEIL
      };
    }
    const {
      min,
      max
    } = getDisplayPriceBoundsForPlan(selectedPlan, planPriceBoundsUsd, currencyList, defaultCurrency?.code ?? 'USD');
    const lo = Math.max(0, Math.floor(min));
    return {
      min: lo,
      max: Math.max(lo + 1, Math.ceil(max))
    };
  }, [planSelected, selectedPlan, planPriceBoundsUsd, currencyList, defaultCurrency?.code]);
  const priceStep = useMemo(() => priceStepFor(priceBounds.min, priceBounds.max), [priceBounds]);

  // Sync state when context changes (e.g. when modal opens after URL hydration)
  useEffect(() => {
    if (!open) return;
    setDraftGuests(Number(filterValues.filterCapOrQtyGte) || 1);
    setPriceError(null);
    setMinInputText(null);
    setMaxInputText(null);
    setPlanRequiredWarning(false);
    const hasApplied = filterValues.priceGte != null || filterValues.priceLte != null;
    setPriceTouched(hasApplied);
    if (hasApplied) {
      // Stored/URL values are USD; the slider works in display currency.
      const code = defaultCurrency?.code ?? 'USD';
      setPriceRange([filterValues.priceGte != null ? convertPriceFromUsd(currencyList, code, Number(filterValues.priceGte)) : priceBounds.min, filterValues.priceLte != null ? convertPriceFromUsd(currencyList, code, Number(filterValues.priceLte)) : priceBounds.max]);
    }
  }, [open]);

  // Keep the slider consistent with the plan's bounds. Runs when the plan or
  // currency changes, and again once Remote Config resolves after opening.
  useEffect(() => {
    if (!open) return;
    if (!priceTouched) {
      setPriceRange([priceBounds.min, priceBounds.max]);
      return;
    }
    // A range restored from the URL/context must still fit the bounds.
    setPriceRange(prev => {
      const [lo, hi] = prev;
      const clamp = (n: number) => Math.min(Math.max(n, priceBounds.min), priceBounds.max);
      const nextLo = clamp(lo);
      const nextHi = clamp(hi);
      return nextLo === lo && nextHi === hi ? prev : [nextLo, nextHi];
    });
  }, [open, priceTouched, priceBounds.min, priceBounds.max]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);
  const selectedTypes = useMemo(() => {
    const v = filterValues.typeIn;
    if (Array.isArray(v)) return v.map(String);
    if (typeof v === 'string' && v.length) return v.split(',');
    return [];
  }, [filterValues.typeIn]);
  const selectedPayments = useMemo(() => {
    const v = filterValues.paymentTypeIn;
    if (Array.isArray(v)) return v.map(String);
    if (typeof v === 'string' && v.length) return v.split(',');
    return [];
  }, [filterValues.paymentTypeIn]);
  const selectedAmenities = useMemo(() => {
    const v = filterValues.amenitiesIdIn;
    if (Array.isArray(v)) return v.map(String);
    if (typeof v === 'string' && v.length) return v.split(',');
    return [];
  }, [filterValues.amenitiesIdIn]);

  /**
   * Adds the price range only when the user narrowed it, and only for the
   * edge they actually moved — an untouched edge sitting on the plan's bound
   * must not exclude listings priced outside Remote Config's estimate.
   */
  const attachPrice = useCallback((next: Record<string, string>): Record<string, string> => {
    if (!planSelected || priceError || !priceTouched) return next;
    const [lo, hi] = priceRange;
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) return next;
    // price_gte/price_lte are compared against USD unified prices. The
    // API's `currency` param 502s for every value (AED/USD/EGP alike),
    // so the slider's display-currency values are converted here.
    const code = defaultCurrency?.code ?? 'USD';
    if (lo > priceBounds.min) {
      next.priceGte = String(convertPriceToUsd(currencyList, code, lo));
    }
    if (hi < priceBounds.max) {
      next.priceLte = String(convertPriceToUsd(currencyList, code, hi));
    }
    return next;
  }, [planSelected, priceError, priceTouched, priceRange, priceBounds, currencyList, defaultCurrency?.code]);
  const buildApplied = useCallback(() => attachPrice(buildAppliedFilters(filterValues, draftSubscriptions)), [attachPrice, filterValues, draftSubscriptions]);
  const previewFilterKey = useMemo(() => {
    if (!open || priceError) return '';
    return JSON.stringify(buildApplied());
  }, [open, priceError, buildApplied]);
  useEffect(() => {
    if (!open) {
      previewRequestRef.current += 1;
      setPreviewResultCount(null);
      setPreviewCountLoading(false);
      return;
    }
    const cleaned = buildApplied();
    const hasLocation = !!(country && city) || !!country || !!geoCountryCode || geoLat != null && geoLog != null;
    if (!hasLocation || !previewFilterKey) {
      setPreviewResultCount(null);
      setPreviewCountLoading(false);
      return;
    }
    const requestId = ++previewRequestRef.current;
    setPreviewCountLoading(true);
    const timeout = setTimeout(async () => {
      if (requestId !== previewRequestRef.current || !openRef.current) {
        return;
      }
      try {
        const mode = resolveActiveCarousel(cleaned);
        const apiConfig = await new ApiConfigration().getApiConfig(defaultLanguage);
        const service = new ListingService(apiConfig);
        const location = {
          country,
          city,
          geoLat,
          geoLog,
          geoCountryCode,
          geoRegion,
          radius
        };
        const withIwgQuotes = mode === Carousel.listing && isLongTermFrequency(cleaned.frequency);
        const params = buildPreviewRequestParams(service, mode, cleaned, withIwgQuotes ? {
          ...location,
          limit: PREVIEW_IWG_QUOTE_LIMIT
        } : location);
        if (!params) {
          if (requestId === previewRequestRef.current && openRef.current) {
            setPreviewResultCount(null);
            setPreviewCountLoading(false);
          }
          return;
        }
        const quoteParams = withIwgQuotes ? buildPreviewRequestParams(service, Carousel.listing, {
          ...cleaned,
          frequency: LONG_TERM_IWG_QUOTE_FREQUENCY
        }, {
          ...location,
          limit: PREVIEW_IWG_QUOTE_LIMIT
        }) : undefined;
        const [res, quoteRes] = await Promise.all([service.getallListings(params), quoteParams ? service.getallListings(quoteParams).catch(err => {
          console.error('Preview IWG quote fetch failed', err);
          return undefined;
        }) : undefined]);
        if (requestId !== previewRequestRef.current || !openRef.current) {
          return;
        }

        // Same merge as ExploreClient: IWG/Regus without a month rate,
        // not already in the primary month/annual results.
        // Past the page size only the API `count` is complete, so use it.
        let iwgQuoteExtras = 0;
        if ((quoteRes?.count ?? 0) > PREVIEW_IWG_QUOTE_LIMIT) {
          iwgQuoteExtras = quoteRes?.count ?? 0;
        } else if (quoteRes?.results?.length) {
          const primaryIds = new Set((res?.results || []).map(item => item.id).filter(id => id != null));
          iwgQuoteExtras = filterIwgQuoteOnlyListings(quoteRes.results, listing => getListingProviderCode(listing) === 'iwg').filter(item => item.id != null && !primaryIds.has(item.id)).length;
        }
        setPreviewResultCount((res?.count ?? 0) + iwgQuoteExtras);
      } catch {
        if (requestId === previewRequestRef.current && openRef.current) {
          setPreviewResultCount(null);
        }
      } finally {
        if (requestId === previewRequestRef.current && openRef.current) {
          setPreviewCountLoading(false);
        }
      }
    }, 500);
    return () => {
      clearTimeout(timeout);
      previewRequestRef.current += 1;
    };
  }, [open, previewFilterKey, country, city, geoLat, geoLog, geoCountryCode, geoRegion, radius, defaultLanguage, defaultCurrency?.code]);
  const selectedSubscriptions = draftSubscriptions;
  const toggleType = (id: string) => {
    const next = selectedTypes.includes(id) ? selectedTypes.filter(v => v !== id) : [...selectedTypes, id];
    setFilterValues((prev: FilterValues) => ({
      ...prev,
      typeIn: next
    }));
    firebaseAnalytics('listing_type_filter_applied', {
      listing_type: next
    });
  };
  const togglePayment = (value: string) => {
    const next = selectedPayments.includes(value) ? selectedPayments.filter(v => v !== value) : [...selectedPayments, value];
    setFilterValues((prev: FilterValues) => ({
      ...prev,
      paymentTypeIn: next
    }));
    firebaseAnalytics('pay_method_filter_applied', {
      methodtypes: next
    });
  };
  const toggleAmenity = (id: string) => {
    const next = selectedAmenities.includes(id) ? selectedAmenities.filter(v => v !== id) : [...selectedAmenities, id];
    setFilterValues((prev: FilterValues) => ({
      ...prev,
      amenitiesIdIn: next
    }));
    firebaseAnalytics('amenity_filter_applied', {
      amenity: next
    });
  };
  const toggleSubscription = (subscriptionIds: string[]) => {
    const isSelected = subscriptionIds.some(id => selectedSubscriptions.includes(id));
    const next = isSelected ? selectedSubscriptions.filter(v => !subscriptionIds.includes(v)) : [...selectedSubscriptions, ...subscriptionIds.filter(id => !selectedSubscriptions.includes(id))];
    setDraftSubscriptions(next);
    firebaseAnalytics('subscription_filter_applied', {
      subscription: next
    });
  };
  const setPlan = (value: string) => {
    setFilterValues((prev: FilterValues) => ({
      ...prev,
      frequency: value
    }));
    // Bounds are per-plan: a range picked for "hour" is meaningless for
    // "month", so reset to the new plan's full range.
    setPriceTouched(false);
    setPriceError(null);
    setMinInputText(null);
    setMaxInputText(null);
    setPlanRequiredWarning(false);
    firebaseAnalytics('booking_plan_filter_applied', {
      booking_plan: value
    });
  };
  const changeGuests = (next: number) => {
    const bounded = Math.max(1, Math.min(1000, next));
    setDraftGuests(bounded);
    setFilterValues((prev: FilterValues) => ({
      ...prev,
      filterCapOrQtyGte: bounded
    }));
    firebaseAnalytics('guest_number_filter_applied', {
      num_of_guests: bounded
    });
  };

  // Price stays local to the modal while the user drags — pushing it into
  // FilterContext per step re-rendered every explore consumer (grid + map).
  const handlePriceRange = (values: [number, number]) => {
    setPriceRange(values);
    setPriceTouched(true);
    setMinInputText(null);
    setMaxInputText(null);
    setPriceError(values[0] > values[1] ? t('filters.error') : null);
  };
  const handlePriceRangeCommitted = (values: [number, number]) => {
    firebaseAnalytics('price_range_filter_applied', {
      price_min: values[0],
      price_max: values[1]
    });
  };

  /**
   * The controls are disabled until a plan is picked, so they swallow their
   * own clicks — a transparent overlay catches them and explains why.
   */
  const handleLockedPriceClick = () => {
    // Drop the class first so a repeat click replays the pulse animation.
    setPlanRequiredWarning(false);
    requestAnimationFrame(() => setPlanRequiredWarning(true));
    planSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest'
    });
  };

  /** Select the whole value on focus so the first keystroke replaces it. */
  const selectOnFocus = (e: React.FocusEvent<HTMLInputElement>) => e.target.select();

  /** "05" → "5", so typing over a leading 0 doesn't keep it around. */
  const stripLeadingZeros = (raw: string) => raw.replace(/^0+(?=\d)/, '');
  const handleMinInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = stripLeadingZeros(e.target.value);
    setMinInputText(raw);
    setPriceTouched(true);
    // Empty/partial input ("", "-") is left alone until blur so the field
    // can actually be cleared; the range keeps its last valid value.
    const n = Number(raw);
    if (raw === '' || Number.isNaN(n)) return;
    setPriceRange(([, hi]) => [n, hi]);
    setPriceError(n > priceRange[1] ? t('filters.error') : null);
  };
  const handleMaxInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = stripLeadingZeros(e.target.value);
    setMaxInputText(raw);
    setPriceTouched(true);
    const n = Number(raw);
    if (raw === '' || Number.isNaN(n)) return;
    setPriceRange(([lo]) => [lo, n]);
    setPriceError(n < priceRange[0] ? t('filters.error') : null);
  };

  /** On blur, commit a clamped number — a blank field falls back to bounds. */
  const handleMinBlur = () => {
    const raw = minInputText;
    setMinInputText(null);
    if (raw == null) return;
    const parsed = Number(raw);
    const next = raw === '' || Number.isNaN(parsed) ? priceBounds.min : Math.min(Math.max(parsed, priceBounds.min), priceBounds.max);
    setPriceRange(([, hi]) => [next, hi]);
    setPriceError(next > priceRange[1] ? t('filters.error') : null);
  };
  const handleMaxBlur = () => {
    const raw = maxInputText;
    setMaxInputText(null);
    if (raw == null) return;
    const parsed = Number(raw);
    const next = raw === '' || Number.isNaN(parsed) ? priceBounds.max : Math.min(Math.max(parsed, priceBounds.min), priceBounds.max);
    setPriceRange(([lo]) => [lo, next]);
    setPriceError(next < priceRange[0] ? t('filters.error') : null);
  };
  const applyFilters = () => {
    if (priceError) return;
    const cleaned = buildApplied();
    const nextActive = resolveActiveCarousel(cleaned);
    dispatch(setActiveButton(nextActive));
    dispatch(setFilters(cleaned));
    setFilterValues(processFilters(cleaned));
    const urlFilters = {
      ...cleaned
    };
    if (urlFilters.userSubscriptionIdIn === EXPLORE_SUBSCRIPTION_OPT_OUT) {
      delete urlFilters.userSubscriptionIdIn;
    }
    const qs = new URLSearchParams(window.location.search);
    // Replace filter keys from applied set; drop keys that were cleared.
    URL_FILTER_KEYS.forEach(key => qs.delete(key));
    Object.entries(urlFilters).forEach(([k, v]) => {
      if (v != null && v !== '') qs.set(k, String(v));
    });
    qs.set('active', nextActive as string);
    applyGeoToSearchParams(qs, geoLocation);
    window.history.replaceState(null, '', qs.toString() ? `?${qs}` : window.location.pathname);
    dispatch(setListingPage(1));
    dispatch(setWorkspacePage(1));
    const fetchParams = buildExploreFetchParams({
      page: 1,
      type,
      country,
      city,
      geoLocation
    });
    dispatch(getListingData({
      ...fetchParams,
      filters: cleaned
    }));
    dispatch(getWorkspaceData({
      ...fetchParams,
      filters: cleaned
    }));
    firebaseAnalytics('show_clicked');
    onClose();
  };
  const clearAll = () => {
    setFilterValues({});
    setDraftSubscriptions([]);
    const cleared: Record<string, string> = {};
    if (user?.userSubscriptions?.length) {
      cleared.userSubscriptionIdIn = EXPLORE_SUBSCRIPTION_OPT_OUT;
    }
    dispatch(setFilters(cleared));
    // Untouched → the snap effect resets the range for the cleared plan.
    setPriceTouched(false);
    setDraftGuests(1);
    setPriceError(null);
    setMinInputText(null);
    setMaxInputText(null);
    setPlanRequiredWarning(false);
    const qs = new URLSearchParams(window.location.search);
    URL_FILTER_KEYS.forEach(key => qs.delete(key));
    if (activeButton) qs.set('active', activeButton);
    applyGeoToSearchParams(qs, geoLocation);
    window.history.replaceState(null, '', qs.toString() ? `?${qs}` : window.location.pathname);
    dispatch(setListingPage(1));
    dispatch(setWorkspacePage(1));
    const fetchParams = buildExploreFetchParams({
      page: 1,
      type,
      country,
      city,
      geoLocation
    });
    dispatch(getListingData({
      ...fetchParams,
      filters: cleared
    }));
    dispatch(getWorkspaceData({
      ...fetchParams,
      filters: cleared
    }));
    firebaseAnalytics('clear_clicked');
  };
  if (!open) return null;
  if (typeof document === 'undefined') return null;
  return createPortal(<div className={styles.backdrop} onClick={onClose} role="dialog" aria-modal="true" aria-label={t('filters.filters')}>
            <div className={styles.modal} onClick={e => e.stopPropagation()}>
                <div className={styles.header}>
                    <h2>{t('filters.filters')}</h2>
                    <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
                        <X size={20} />
                    </button>
                </div>

                <div className={styles.body}>
                    {!!subscriptionName && <section className={styles.section}>
                            <div className={styles.twoCol}>
                                {subscriptionOptions.map(opt => <Checkbox key={opt.subscriptionIds.join('-')} checked={opt.subscriptionIds.some(id => selectedSubscriptions.includes(id))} onChange={() => toggleSubscription(opt.subscriptionIds)}>
                                        <span className={styles.optionIcon}>
                                            <Image src="/assets/icons/user-placeholder-v4.svg" alt="" width={22} height={22} unoptimized />
                                        </span>
                                        <span className={styles.optionLabel}>
                                            {t('filters.showsub')}{' '}
                                            <span>{opt.packageName}</span>{' '}
                                            {t('filters.subscription')}
                                        </span>
                                    </Checkbox>)}
                            </div>
                        </section>}
                    {/* Listing type */}
                    <section className={styles.section}>
                        <h3 className={styles.sectionLabel}>
                            {t('filters.type')}
                        </h3>
                        <div className={styles.twoCol}>
                            {listingRes.map(opt => {
              const listing = opt as ListingType & {
                url?: string;
                svg?: string;
              };
              const id = listing.id?.toString() || '';
              const iconSrc = listing.url || listing.svg;
              return <Checkbox key={id} checked={selectedTypes.includes(id)} onChange={() => toggleType(id)}>
                                        {iconSrc && <span className={styles.optionIcon}>
                                                <Image src={iconSrc.toString()} alt="" width={18} height={18} unoptimized />
                                            </span>}
                                        <span className={styles.optionLabel}>
                                            {opt.type}
                                        </span>
                                    </Checkbox>;
            })}
                        </div>
                    </section>

                    {/* Suggested: Top rated + Instant booking (`quickBooking`) */}
                    <section className={styles.section}>
                        <h3 className={styles.sectionLabel}>
                            {t('filters.suggested.title')}
                        </h3>
                        <div className={styles.twoCol}>
                            <Checkbox checked={isQuickBookingActive(filterValues.quickBooking)} onChange={() => setFilterValues((prev: FilterValues) => {
              const enable = !isQuickBookingActive(prev.quickBooking);
              if (enable) {
                firebaseAnalytics('instant_conf_filter_applied');
              }
              return {
                ...prev,
                quickBooking: enable
              };
            })}>
                                <span className={styles.optionIcon}>
                                    <Zap size={16} fill="currentColor" aria-hidden="true" />
                                </span>
                                <span className={styles.optionLabel}>
                                    {t('filters.suggested.instantBooking')}
                                </span>
                            </Checkbox>
                            <Checkbox checked={(filterValues as any).ratingGte === '4'} onChange={() => setFilterValues((prev: FilterValues) => ({
              ...prev,
              ratingGte: (prev as any).ratingGte === '4' ? '' : '4'
            }) as any)}>
                                <span className={styles.optionIcon}>
                                    <Star size={16} />
                                </span>
                                <span className={styles.optionLabel}>
                                    {t('filters.suggested.topRated')}
                                </span>
                            </Checkbox>
                        </div>
                    </section>

                    {/* Guests */}
                    <section className={styles.section}>
                        <h3 className={styles.sectionLabel}>
                            {t('filters.listing-type.guest')}
                        </h3>
                        <div className={styles.counterRow}>
                            <span className={styles.counterAvatar}>
                                <User size={16} />
                            </span>
                            <div className={styles.counter}>
                                <button type="button" onClick={() => changeGuests(draftGuests - 1)} disabled={draftGuests <= 1} aria-label="Decrease">
                                    −
                                </button>
                                <span className={styles.counterValue}>
                                    {draftGuests}
                                </span>
                                <button type="button" onClick={() => changeGuests(draftGuests + 1)} aria-label="Increase">
                                    +
                                </button>
                            </div>
                        </div>
                    </section>

                    {/* Plan */}
                    <section ref={planSectionRef} className={`${styles.section} ${planRequiredWarning ? styles.sectionFlagged : ''}`}>
                        <h3 className={styles.sectionLabel}>
                            {t('filters.plan-type')}
                        </h3>
                        <div className={styles.twoCol}>
                            {PLAN_OPTIONS.map(opt => <Radio key={opt.value} checked={(filterValues.frequency || '').split(',').includes(opt.value)} onChange={() => setPlan(opt.value)}>
                                    <span className={styles.optionIcon}>
                                        {'iconSrc' in opt && opt.iconSrc ?
                // eslint-disable-next-line @next/next/no-img-element
                <img src={opt.iconSrc} alt="" width={16} height={16} /> : <Clock size={16} />}
                                    </span>
                                    <span className={styles.optionLabel}>
                                        {t(opt.labelKey)}
                                    </span>
                                </Radio>)}
                        </div>
                    </section>

                    {/* Price range */}
                    <section className={styles.section}>
                        <h3 className={styles.sectionLabel}>
                            {t('filters.price')}
                        </h3>
                        {!planSelected && <p className={`${styles.sectionHint} ${planRequiredWarning ? styles.sectionHintWarning : ''}`} role="status" aria-live="polite">
                                {t('filters.price-range.label')}
                            </p>}
                        <div className={styles.sliderWrap}>
                            {!planSelected && <button type="button" className={styles.lockedOverlay} onClick={handleLockedPriceClick} aria-label={t('filters.price-range.label')} />}
                            <Slider className={styles.slider} thumbClassName={styles.thumb} trackClassName={styles.track} renderTrack={(props: any, state: any) => <div {...props} className={`${styles.track} ${state.index === 1 ? styles.trackActive : ''}`} />} min={priceBounds.min} max={priceBounds.max} step={priceStep} value={priceRange} onChange={handlePriceRange} onAfterChange={handlePriceRangeCommitted} pearling minDistance={priceStep} disabled={!planSelected} />
                            <div className={styles.priceInputs}>
                                <div className={styles.priceInputBox}>
                                    <label>
                                        {t('filters.price-range.min')}
                                    </label>
                                    <input type="number" value={minInputText ?? priceRange[0]} min={priceBounds.min} max={priceBounds.max} onChange={handleMinInput} onFocus={selectOnFocus} onBlur={handleMinBlur} disabled={!planSelected} />
                                    <span className={styles.currencyCode}>
                                        {defaultCurrency?.code || ''}
                                    </span>
                                </div>
                                <span className={styles.priceDash}>—</span>
                                <div className={styles.priceInputBox}>
                                    <label>
                                        {t('filters.price-range.max')}
                                    </label>
                                    <input type="number" value={maxInputText ?? priceRange[1]} min={priceBounds.min} max={priceBounds.max} onChange={handleMaxInput} onFocus={selectOnFocus} onBlur={handleMaxBlur} disabled={!planSelected} />
                                    <span className={styles.currencyCode}>
                                        {defaultCurrency?.code || ''}
                                    </span>
                                </div>
                            </div>
                            {priceError && <p className={styles.errorMessage}>
                                    {priceError}
                                </p>}
                        </div>
                    </section>

                    {/* Payment */}
                    <section className={styles.section}>
                        <h3 className={styles.sectionLabel}>
                            {t('filters.payment')}
                        </h3>
                        <div className={styles.twoCol}>
                            {PAYMENT_OPTIONS.map(opt => <Checkbox key={opt.value} checked={selectedPayments.includes(opt.value)} onChange={() => togglePayment(opt.value)}>
                                    <span className={styles.optionLabel}>
                                        {t(opt.labelKey)}
                                    </span>
                                </Checkbox>)}
                        </div>
                    </section>

                    {/* Amenities */}
                    <section className={styles.section}>
                        <h3 className={styles.sectionLabel}>
                            {t('filters.aminities')}
                        </h3>
                        <div className={`${styles.twoCol} ${styles.amenitiesList}`}>
                            {allAminities.map(a => {
              const id = a.id?.toString() || '';
              return <Checkbox key={id} checked={selectedAmenities.includes(id)} onChange={() => toggleAmenity(id)}>
                                        {a.url && <span className={styles.optionIcon}>
                                                <Image src={a.url.toString()} alt="" width={16} height={16} />
                                            </span>}
                                        <span className={styles.optionLabel}>
                                            {a.name}
                                        </span>
                                    </Checkbox>;
            })}
                        </div>
                    </section>
                </div>

                <div className={styles.footer}>
                    <button type="button" className={styles.clearBtn} onClick={clearAll}>
                        {t('filters.clearAll')}
                    </button>
                    <button type="button" className={styles.applyBtn} onClick={applyFilters} disabled={!!priceError || previewCountLoading}>
                        {previewCountLoading && <Loader2 size={16} className={styles.applySpinner} aria-hidden="true" />}
                        <span>
                            {t('filters.showResults')}
                            {!previewCountLoading && previewResultCount != null ? ` (${previewResultCount})` : ''}
                        </span>
                    </button>
                </div>
            </div>
        </div>, document.body);
};
export default ExploreFiltersModal;